import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Save, User, Camera, Trophy, Video, Image, Info, Shield, X, UserCheck } from 'lucide-react';
import { sportsService, Sport } from '@/services/sportsService';
import { internalProfileService } from '@/services/internalProfileService';
import { supabase } from '@/integrations/supabase/client';
import { photoUploadService } from '@/services/photoUploadService';
import { playerMediaService } from '@/services/playerMediaService';
import VideoPlayer from '@/components/media/VideoPlayer';
import PhotoGallery from '@/components/media/PhotoGallery';
import { usePoints } from '@/hooks/usePoints';
import { childPrivacyService } from '@/services/childPrivacyService';

// Sport-specific positions mapping
const SPORT_POSITIONS = {
  'Football': [
    'Quarterback', 'Running Back', 'Wide Receiver', 'Tight End', 'Offensive Line',
    'Defensive Line', 'Linebacker', 'Cornerback', 'Safety', 'Kicker', 'Punter'
  ],
  'Basketball': [
    'Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'
  ],
  'Soccer': [
    'Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'Striker', 'Winger'
  ],
  'Baseball': [
    'Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base', 'Shortstop',
    'Left Field', 'Center Field', 'Right Field', 'Designated Hitter'
  ],
  'Tennis': [
    'Singles Player', 'Doubles Player'
  ],
  'Golf': [
    'Individual Player'
  ],
  'Swimming': [
    'Freestyle', 'Backstroke', 'Breaststroke', 'Butterfly', 'Individual Medley', 'Relay'
  ],
  'Track & Field': [
    'Sprinter', 'Middle Distance', 'Long Distance', 'Hurdler', 'Jumper', 'Thrower', 'Multi-Event'
  ],
  'Volleyball': [
    'Outside Hitter', 'Middle Blocker', 'Setter', 'Libero', 'Opposite Hitter', 'Defensive Specialist'
  ],
  'Hockey': [
    'Goalie', 'Defenseman', 'Left Wing', 'Right Wing', 'Center'
  ],
  'Softball': [
    'Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base', 'Shortstop',
    'Left Field', 'Center Field', 'Right Field', 'Designated Player'
  ],
  'Wrestling': [
    'Lightweight', 'Middleweight', 'Heavyweight'
  ],
  'Cross Country': [
    'Distance Runner'
  ],
  'Gymnastics': [
    'All-Around', 'Vault', 'Uneven Bars', 'Balance Beam', 'Floor Exercise',
    'Pommel Horse', 'Still Rings', 'Parallel Bars', 'High Bar'
  ],
  'Lacrosse': [
    'Attack', 'Midfield', 'Defense', 'Goalie'
  ],
  'Water Polo': [
    'Goalkeeper', 'Defender', 'Midfielder', 'Attacker'
  ],
  'Badminton': [
    'Singles Player', 'Doubles Player'
  ],
  'Bowling': [
    'Individual Player'
  ],
  'Cheerleading': [
    'Base', 'Flyer', 'Spotter', 'Tumbler'
  ],
  'Cricket': [
    'Batsman', 'Bowler', 'All-Rounder', 'Wicket Keeper'
  ],
  'Cycling': [
    'Road Cyclist', 'Track Cyclist', 'Mountain Biker', 'BMX Rider'
  ],
  'Fencing': [
    'Foil', 'Épée', 'Sabre'
  ],
  'Field Hockey': [
    'Goalkeeper', 'Defender', 'Midfielder', 'Forward'
  ],
  'Figure Skating': [
    'Singles Skater', 'Pairs Skater', 'Ice Dancer'
  ],
  'Martial Arts': [
    'Competitor', 'Practitioner'
  ],
  'Rowing': [
    'Coxswain', 'Rower'
  ],
  'Rugby': [
    'Forward', 'Back', 'Scrum Half', 'Fly Half'
  ],
  'Sailing': [
    'Skipper', 'Crew'
  ],
  'Skiing': [
    'Alpine Skier', 'Cross Country Skier', 'Freestyle Skier'
  ],
  'Surfing': [
    'Shortboard', 'Longboard', 'Stand-up Paddle'
  ],
  'Table Tennis': [
    'Singles Player', 'Doubles Player'
  ],
  'Ultimate Frisbee': [
    'Handler', 'Cutter', 'Hybrid'
  ]
};

const EnhancedPlayerProfileForm: React.FC = () => {
  const { user: supabaseUser, profile, refreshProfile } = useAuth();
  const { user: internalUser, profile: internalProfile, refreshProfile: refreshInternalProfile } = useInternalAuth();
  const { checkAndAwardProfileCompletion } = usePoints();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [availableSports, setAvailableSports] = useState<Sport[]>([]);
  const [sportsLoaded, setSportsLoaded] = useState(false);
  const [highlightVideoLinks, setHighlightVideoLinks] = useState<string[]>(['', '', '', '', '', '']);
  const [photoGalleryLinks, setPhotoGalleryLinks] = useState<string[]>(['', '', '', '', '', '']);
  
  const [formData, setFormData] = useState({
    // Basic Information
    first_name: '',
    last_name: '',
    username: '',
    phone: '',
    
    // Sport and Season Information
    current_sport: '',
    season_status: 'off-season',
    
    // Player Bio
    player_bio: '',
    
    // Physical Information
    height: '',
    weight: '',
    fitness_level: '',
    
    // Academic Information
    grade_level: '',
    
    // Athletic Information
    position: '',
    jersey_number: '',
    skill_level: '',
    years_playing: '',
    achievements: '',
    
    // Media
    highlight_video_links: [] as string[],
    photo_gallery_links: [] as string[],
    
    // Profile Photo
    profile_photo: null as File | null,
    profile_photo_url: '',
    
    // Privacy Settings
    show_profile_public: false,
    show_contact_public: false,
  });

  useEffect(() => {
    loadAvailableSports();
  }, []);

  useEffect(() => {
    if ((profile || internalProfile) && sportsLoaded) {
      initializeFormData();
      loadExistingMedia();
    }
  }, [profile, internalProfile, internalUser, supabaseUser, sportsLoaded]);

  const loadExistingMedia = async () => {
    const currentUserId = internalUser?.id || supabaseUser?.id;
    const userType = internalUser ? 'internal' : 'supabase';
    
    if (currentUserId) {
      try {
        // Load existing videos
        const videosResult = await playerMediaService.getHighlightVideos(currentUserId, userType);
        if (videosResult.success && videosResult.data) {
          const videoUrls = videosResult.data.map((video: any) => video.video_url || '');
          const paddedVideos = [...videoUrls];
          while (paddedVideos.length < 6) paddedVideos.push('');
          setHighlightVideoLinks(paddedVideos);
        }

        // Load existing photos
        const photosResult = await playerMediaService.getPhotoGallery(currentUserId, userType);
        if (photosResult.success && photosResult.data) {
          const photoUrls = photosResult.data.map((photo: any) => photo.photo_url || '');
          const paddedPhotos = [...photoUrls];
          while (paddedPhotos.length < 6) paddedPhotos.push('');
          setPhotoGalleryLinks(paddedPhotos);
        }
      } catch (error) {
        console.error('Error loading existing media:', error);
      }
    }
  };

  const initializeFormData = () => {
    // Get current user data
    const currentUser = internalUser || supabaseUser;
    const currentProfile = internalProfile || profile;
    
    console.log('=== FORM INITIALIZATION ===');
    console.log('Current user:', currentUser);
    console.log('Current profile:', currentProfile);
    console.log('Is internal user:', !!internalUser);
    console.log('Available sports:', availableSports.map(s => s.sport_name));
    console.log('Profile current_sport:', currentProfile?.current_sport);
    
    if (currentUser || currentProfile) {
      // Get username based on user type
      const getUserUsername = () => {
        if (internalUser) {
          return internalUser.username || '';
        }
        if (currentProfile?.username) {
          return currentProfile.username;
        }
        return '';
      };

      const newFormData = {
        first_name: currentProfile?.first_name || '',
        last_name: currentProfile?.last_name || '',
        username: getUserUsername(),
        phone: currentProfile?.phone || '',
        
        // Sport and Season Information - Fixed initialization with proper fallbacks
        current_sport: currentProfile?.current_sport || currentProfile?.primary_sport || '',
        season_status: currentProfile?.season_status || 'off-season',
        
        player_bio: currentProfile?.player_bio || currentProfile?.bio || '',
        
        // Physical Information - Fixed initialization with proper fallbacks
        height: currentProfile?.height || '',
        weight: currentProfile?.weight || '',
        fitness_level: currentProfile?.fitness_level || '',
        
        // Academic Information - Fixed initialization with proper fallbacks
        grade_level: currentProfile?.grade_level || '',
        
        // Athletic Information - Fixed initialization with proper fallbacks
        position: currentProfile?.position || '',
        jersey_number: currentProfile?.jersey_number?.toString() || '',
        skill_level: currentProfile?.skill_level || '',
        years_playing: currentProfile?.years_playing?.toString() || '',
        achievements: currentProfile?.achievements || '',
        
        highlight_video_links: currentProfile?.highlight_video_links || [],
        photo_gallery_links: currentProfile?.photo_gallery_links || [],
        
        profile_photo: null,
        profile_photo_url: currentProfile?.profile_photo_url || '',
        
        show_profile_public: currentProfile?.show_profile_public || false,
        show_contact_public: currentProfile?.show_contact_public || false,
      };
      
      console.log('Setting form data:', newFormData);
      setFormData(newFormData);

      // Initialize highlight video links
      if (currentProfile?.highlight_video_links && currentProfile.highlight_video_links.length > 0) {
        const videos = [...currentProfile.highlight_video_links];
        while (videos.length < 6) videos.push('');
        setHighlightVideoLinks(videos);
      }

      // Initialize photo gallery links
      if (currentProfile?.photo_gallery_links && currentProfile.photo_gallery_links.length > 0) {
        const photos = [...currentProfile.photo_gallery_links];
        while (photos.length < 6) photos.push('');
        setPhotoGalleryLinks(photos);
      }

      // Load profile photo preview
      if (currentProfile?.profile_photo_url) {
        loadProfilePhotoPreview(currentProfile.profile_photo_url);
      }
    }
  };

  const loadProfilePhotoPreview = async (photoPath: string) => {
    try {
      const signedUrl = await photoUploadService.getProfilePhotoUrl(photoPath);
      if (signedUrl) {
        setPhotoPreview(signedUrl);
      }
    } catch (error) {
      console.error('Error loading profile photo:', error);
    }
  };

  // Reset position when sport changes
  useEffect(() => {
    if (formData.current_sport && formData.position) {
      const availablePositions = SPORT_POSITIONS[formData.current_sport as keyof typeof SPORT_POSITIONS] || [];
      if (!availablePositions.includes(formData.position)) {
        setFormData(prev => ({ ...prev, position: '' }));
      }
    }
  }, [formData.current_sport]);

  const loadAvailableSports = async () => {
    const sports = await sportsService.getAvailableSports();
    setAvailableSports(sports);
    setSportsLoaded(true);
  };

  const handleInputChange = (field: string, value: any) => {
    // Block privacy setting changes for under-13 users
    const userAgeGroup = internalUser?.age_group || internalProfile?.age_group || profile?.age_group;
    const isUnder13 = childPrivacyService.isUserUnder13(userAgeGroup);
    
    if (isUnder13 && (field === 'show_profile_public' || field === 'show_contact_public')) {
      console.log('Blocked privacy change attempt for under-13 user');
      return;
    }
    
    console.log(`Changing ${field} to:`, value);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, profile_photo: file }));
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleHighlightVideoChange = (index: number, value: string) => {
    const newLinks = [...highlightVideoLinks];
    newLinks[index] = value;
    setHighlightVideoLinks(newLinks);
    setFormData(prev => ({ 
      ...prev, 
      highlight_video_links: newLinks.filter(link => link.trim() !== '') 
    }));
  };

  const handlePhotoGalleryChange = (index: number, value: string) => {
    const newLinks = [...photoGalleryLinks];
    newLinks[index] = value;
    setPhotoGalleryLinks(newLinks);
    setFormData(prev => ({ 
      ...prev, 
      photo_gallery_links: newLinks.filter(link => link.trim() !== '') 
    }));
  };

  const removeHighlightVideo = (index: number) => {
    const newLinks = [...highlightVideoLinks];
    newLinks[index] = '';
    setHighlightVideoLinks(newLinks);
    setFormData(prev => ({ 
      ...prev, 
      highlight_video_links: newLinks.filter(link => link.trim() !== '') 
    }));
  };

  const removePhotoGallery = (index: number) => {
    const newLinks = [...photoGalleryLinks];
    newLinks[index] = '';
    setPhotoGalleryLinks(newLinks);
    setFormData(prev => ({ 
      ...prev, 
      photo_gallery_links: newLinks.filter(link => link.trim() !== '') 
    }));
  };

  const uploadProfilePhoto = async (file: File): Promise<string | null> => {
    try {
      const currentUserId = internalUser?.id || supabaseUser?.id;
      if (!currentUserId) return null;

      const result = await photoUploadService.uploadProfilePhoto(file, currentUserId);
      if (result.success && result.url) {
        return result.url;
      }
      return null;
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      return null;
    }
  };

  const isValidVideoUrl = (url: string): boolean => {
    const videoRegex = /(youtube\.com|youtu\.be|vimeo\.com|twitch\.tv|dailymotion\.com)/i;
    return videoRegex.test(url);
  };

  const isValidImageUrl = (url: string): boolean => {
    // Updated regex to be more permissive for image URLs
    const imageRegex = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i;
    return imageRegex.test(url) || 
           url.includes('imgur.com') || 
           url.includes('cloudinary.com') || 
           url.includes('unsplash.com') || 
           url.includes('pexels.com') || 
           url.includes('pixabay.com') || 
           url.includes('freepik.com') ||
           url.includes('istockphoto.com') ||
           url.includes('shutterstock.com');
  };

  const renderVideoPlayer = (url: string, index: number) => {
    if (!url || !isValidVideoUrl(url)) return null;

    let embedUrl = '';
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('youtu.be/')[1]?.split('?')[0]
        : url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
    } else if (url.includes('vimeo.com')) {
      const videoId = url.split('vimeo.com/')[1]?.split('/')[0];
      if (videoId) {
        embedUrl = `https://player.vimeo.com/video/${videoId}`;
      }
    }

    if (!embedUrl) return null;

    return (
      <div key={index} className="relative">
        <iframe
          src={embedUrl}
          className="w-full h-48 rounded-lg"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  };

  const renderPhotoGallery = (url: string, index: number) => {
    if (!url || !isValidImageUrl(url)) return null;

    return (
      <div key={index} className="relative">
        <img
          src={url}
          alt={`Gallery ${index + 1}`}
          className="w-full h-48 object-cover rounded-lg"
          onError={(e) => {
            console.error(`Failed to load image: ${url}`);
            e.currentTarget.style.display = 'none';
          }}
        />
      </div>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    console.log('=== FORM SUBMISSION START ===');
    console.log('Form data being submitted:', formData);

    try {
      let profilePhotoUrl = formData.profile_photo_url;
      
      // Upload profile photo if one was selected
      if (formData.profile_photo) {
        console.log('Uploading profile photo...');
        const uploadedPath = await uploadProfilePhoto(formData.profile_photo);
        if (uploadedPath) {
          profilePhotoUrl = uploadedPath;
          console.log('Profile photo uploaded to:', profilePhotoUrl);
        }
      }

      // Get the current user ID
      const currentUserId = internalUser?.id || supabaseUser?.id;
      const userType = internalUser ? 'internal' : 'supabase';
      
      if (!currentUserId) {
        toast.error('User not authenticated');
        return;
      }

      console.log('Saving profile for user:', currentUserId, 'Type:', userType);

      // Save highlight videos and photo gallery separately (continue even if they fail)
      const videoLinksToSave = highlightVideoLinks.filter(link => link && link.trim() !== '');
      const photoLinksToSave = photoGalleryLinks.filter(link => link && link.trim() !== '');

      console.log('Saving video links:', videoLinksToSave);
      console.log('Saving photo links:', photoLinksToSave);

      // Save media to separate tables - continue even if these fail
      try {
        await playerMediaService.saveHighlightVideos(currentUserId, userType, videoLinksToSave);
      } catch (error) {
        console.warn('Video save error (continuing):', error);
      }

      try {
        await playerMediaService.savePhotoGallery(currentUserId, userType, photoLinksToSave);
      } catch (error) {
        console.warn('Photo save error (continuing):', error);
      }

      // Prepare the profile data based on user type
      if (internalUser) {
        // For internal users - save to internal_user_profiles table
        const profileData = {
          user_id: currentUserId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          username: formData.username,
          phone: formData.phone,
          bio: formData.player_bio,
          profile_photo_url: profilePhotoUrl,
          
          // Sport and Season Information - Ensure proper mapping
          current_sport: formData.current_sport,
          season_status: formData.season_status,
          
          // Player Bio
          player_bio: formData.player_bio,
          
          // Physical Information - Ensure proper mapping
          height: formData.height,
          weight: formData.weight,
          fitness_level: formData.fitness_level,
          
          // Academic Information - Ensure proper mapping
          grade_level: formData.grade_level,
          
          // Athletic Information - Ensure proper mapping
          position: formData.position,
          jersey_number: formData.jersey_number ? parseInt(formData.jersey_number) : null,
          years_playing: formData.years_playing ? parseInt(formData.years_playing) : null,
          skill_level: formData.skill_level,
          achievements: formData.achievements,
          
          // Media links
          highlight_video_links: videoLinksToSave,
          photo_gallery_links: photoLinksToSave,
          
          // Privacy Settings
          show_profile_public: formData.show_profile_public,
          show_contact_public: formData.show_contact_public,
          
        };

        console.log('Saving internal profile data:', profileData);

        const result = await internalProfileService.upsertProfile(profileData);
        if (!result.success) {
          console.error('Internal profile save failed:', result.error);
          throw new Error(result.error);
        }

        console.log('Internal profile saved successfully');
      } else if (supabaseUser) {
        // For Supabase users - save to profiles table
        const updateData = {
          id: currentUserId,
          email: supabaseUser.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          username: formData.username,
          phone: formData.phone,
          
          // Sport and Season Information - Ensure proper mapping
          current_sport: formData.current_sport,
          season_status: formData.season_status,
          
          // Player Bio
          player_bio: formData.player_bio,
          bio: formData.player_bio,
          
          // Physical Information - Ensure proper mapping
          height: formData.height,
          weight: formData.weight,
          fitness_level: formData.fitness_level,
          
          // Academic Information - Ensure proper mapping
          grade_level: formData.grade_level,
          
          // Athletic Information - Ensure proper mapping
          position: formData.position,
          jersey_number: formData.jersey_number ? parseInt(formData.jersey_number) : null,
          skill_level: formData.skill_level,
          years_playing: formData.years_playing ? parseInt(formData.years_playing) : null,
          achievements: formData.achievements,
          
          // Media links
          highlight_video_links: videoLinksToSave,
          photo_gallery_links: photoLinksToSave,
          
          // Profile Photo
          profile_photo_url: profilePhotoUrl,
          
          // Privacy Settings
          show_profile_public: formData.show_profile_public,
          show_contact_public: formData.show_contact_public,
          
        };

        console.log('Saving Supabase profile data:', updateData);

        const { error } = await supabase
          .from('profiles')
          .upsert(updateData, {
            onConflict: 'id'
          });

        if (error) {
          console.error('Supabase profile save failed:', error);
          throw error;
        }

        console.log('Supabase profile saved successfully');
      }

      // Check for profile completion and award points
      const wasAwarded = await checkAndAwardProfileCompletion(
        { ...formData, id: currentUserId, role: 'player' },
        'player'
      );
      
      if (wasAwarded) {
        toast.success('🎉 Profile Complete! +50 Points Earned!', { 
          duration: 5000,
          description: 'Your profile is now 100% complete!'
        });
      } else {
        toast.success('Player profile updated successfully!');
      }
      
      // Check for profile completion and award points if applicable
      try {
        const savedProfile = internalUser ? 
          { ...formData, user_id: currentUserId, role: 'player' } : 
          { ...formData, id: currentUserId, role: 'player' };
        const wasAwarded = await checkAndAwardProfileCompletion(savedProfile, 'player');
        if (wasAwarded) {
          toast.success('Profile completion bonus awarded! +50 points', { duration: 4000 });
        }
      } catch (error) {
        console.error('Error checking profile completion:', error);
      }
      
      console.log('=== FORM SUBMISSION SUCCESS ===');
      
      // Refresh profile data in auth context so header/menu updates without page reload
      if (internalUser) {
        await refreshInternalProfile();
      } else {
        await refreshProfile();
      }
      
    } catch (error) {
      console.error('=== FORM SUBMISSION ERROR ===', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isInSeason = formData.season_status === 'in-season';
  const availablePositions = SPORT_POSITIONS[formData.current_sport as keyof typeof SPORT_POSITIONS] || [];

  // Get profile photo URL - use preview if available, otherwise use saved URL
  const displayPhotoUrl = photoPreview || (formData.profile_photo_url ? formData.profile_photo_url : null);

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Photo Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Profile Photo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                {displayPhotoUrl ? (
                  <img 
                    src={displayPhotoUrl} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="h-12 w-12 text-gray-400" />
                )}
              </div>
              <div>
                <Label htmlFor="profile_photo" className="cursor-pointer">
                  <div className="bg-sports-green text-black px-4 py-2 rounded-lg hover:bg-green-600 transition-colors inline-flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    Upload Photo
                  </div>
                </Label>
                <Input
                  id="profile_photo"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-gray-300">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-gray-300">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="username" className="text-gray-300">Username *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-gray-300">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sport & Season Status */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Sport & Season Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="current_sport" className="text-gray-300">Current Sport *</Label>
                <Select 
                  value={formData.current_sport} 
                  onValueChange={(value) => handleInputChange('current_sport', value)}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select your sport" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600 text-white z-50">
                    {availableSports.map((sport) => (
                      <SelectItem 
                        key={sport.id} 
                        value={sport.sport_name}
                        className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white"
                      >
                        {sport.sport_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="season_status" className="text-gray-300">Season Status *</Label>
                <Select 
                  value={formData.season_status} 
                  onValueChange={(value) => handleInputChange('season_status', value)}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select season status" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600 text-white z-50">
                    <SelectItem value="in-season" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">In Season</SelectItem>
                    <SelectItem value="off-season" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">Off Season</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.season_status && (
              <div className="p-4 bg-gray-700 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="h-4 w-4 text-sports-green" />
                  <span className="text-white font-medium">
                    {formData.season_status === 'in-season' ? 'In Season' : 'Off Season'}
                  </span>
                </div>
                <p className="text-gray-300 text-sm">
                  {formData.season_status === 'in-season'
                    ? 'During in-season, you can submit stats, scores, and game-related information. All features are active.'
                    : 'During off-season, stat submission and game-related features are inactive. Focus on training and preparation.'
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Player Bio */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Player Bio / About Me</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="player_bio" className="text-gray-300">Tell us about yourself</Label>
              <Textarea
                id="player_bio"
                value={formData.player_bio}
                onChange={(e) => handleInputChange('player_bio', e.target.value)}
                placeholder="Share your story, what drives you, your passion for the sport..."
                className="bg-gray-700 border-gray-600 text-white min-h-[120px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Physical Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Physical Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="height" className="text-gray-300">Height</Label>
                <Input
                  id="height"
                  value={formData.height}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                  placeholder="e.g., 5'10&quot;, 178cm"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="weight" className="text-gray-300">Weight</Label>
                <Input
                  id="weight"
                  value={formData.weight}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  placeholder="e.g., 150 lbs, 68 kg"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="fitness_level" className="text-gray-300">Fitness Level</Label>
                <Select 
                  value={formData.fitness_level} 
                  onValueChange={(value) => handleInputChange('fitness_level', value)}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select fitness level" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600 text-white z-50">
                    <SelectItem value="excellent" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">Excellent</SelectItem>
                    <SelectItem value="very-good" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">Very Good</SelectItem>
                    <SelectItem value="good" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">Good</SelectItem>
                    <SelectItem value="fair" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">Fair</SelectItem>
                    <SelectItem value="needs-improvement" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">Needs Improvement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Academic Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Academic Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="grade_level" className="text-gray-300">Grade Level</Label>
              <Select 
                value={formData.grade_level} 
                onValueChange={(value) => handleInputChange('grade_level', value)}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Select grade level" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600 text-white z-50">
                  <SelectItem value="not-specified" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">Not Specified</SelectItem>
                  <SelectItem value="elementary" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">Elementary</SelectItem>
                  <SelectItem value="middle-school" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">Middle School</SelectItem>
                  <SelectItem value="high-school" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">High School</SelectItem>
                  <SelectItem value="college" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">College</SelectItem>
                  <SelectItem value="graduated" className="text-white hover:bg-gray-600 focus:bg-gray-600 focus:text-white">Graduated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Athletic Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Athletic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="position" className="text-gray-300">Position</Label>
                {formData.current_sport && availablePositions.length > 0 ? (
                  <Select 
                    value={formData.position} 
                    onValueChange={(value) => handleInputChange('position', value)}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select your position" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600 text-white z-50">
                      {availablePositions.map((position) => (
                        <SelectItem 
                          key={position} 
                          value={position}
                          className="text-white hover:bg-gray-700 focus:bg-gray-700 focus:text-white"
                        >
                          {position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="position"
                    value={formData.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                    placeholder="Please select a sport first"
                    className="bg-gray-700 border-gray-600 text-white"
                    disabled={!formData.current_sport}
                  />
                )}
              </div>
              <div>
                <Label htmlFor="jersey_number" className="text-gray-300">Jersey Number</Label>
                <Input
                  id="jersey_number"
                  type="number"
                  value={formData.jersey_number}
                  onChange={(e) => handleInputChange('jersey_number', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="skill_level" className="text-gray-300">Skill Level</Label>
                <Select 
                  value={formData.skill_level} 
                  onValueChange={(value) => handleInputChange('skill_level', value)}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select skill level" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600 text-white z-50">
                    <SelectItem value="beginner" className="text-white hover:bg-gray-700 focus:bg-gray-700 focus:text-white">Beginner</SelectItem>
                    <SelectItem value="intermediate" className="text-white hover:bg-gray-700 focus:bg-gray-700 focus:text-white">Intermediate</SelectItem>
                    <SelectItem value="advanced" className="text-white hover:bg-gray-700 focus:bg-gray-700 focus:text-white">Advanced</SelectItem>
                    <SelectItem value="professional" className="text-white hover:bg-gray-700 focus:bg-gray-700 focus:text-white">Professional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="years_playing" className="text-gray-300">Years Playing</Label>
                <Input
                  id="years_playing"
                  type="number"
                  value={formData.years_playing}
                  onChange={(e) => handleInputChange('years_playing', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="achievements" className="text-gray-300">Athletic Accomplishments</Label>
              <Textarea
                id="achievements"
                value={formData.achievements}
                onChange={(e) => handleInputChange('achievements', e.target.value)}
                placeholder="Share your athletic accomplishments, awards, records..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Highlight Videos */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Video className="h-5 w-5" />
              Highlight Video Links
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {highlightVideoLinks.map((link, index) => (
              <div key={index} className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={link}
                    onChange={(e) => handleHighlightVideoChange(index, e.target.value)}
                    placeholder={`Enter video URL ${index + 1} (YouTube, Vimeo, etc.)`}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                  {link && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeHighlightVideo(index)}
                      className="text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {link && <VideoPlayer url={link} />}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Photo Gallery */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Image className="h-5 w-5" />
              Photo Gallery Links
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {photoGalleryLinks.map((link, index) => (
              <div key={index} className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={link}
                    onChange={(e) => handlePhotoGalleryChange(index, e.target.value)}
                    placeholder={`Enter photo URL ${index + 1}`}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                  {link && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removePhotoGallery(index)}
                      className="text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            {/* Photo Gallery Preview */}
            <div className="mt-4">
              <PhotoGallery 
                photos={photoGalleryLinks
                  .filter(link => link.trim() !== '')
                  .map(url => ({ url }))}
              />
            </div>
          </CardContent>
        </Card>

        {/* Privacy Settings - Hidden for under-13 users */}
        {(() => {
          // Priority: internalUser.age_group (most reliable for internal users) > internalProfile > profile
          const userAgeGroup = internalUser?.age_group || internalProfile?.age_group || profile?.age_group;
          const isUnder13 = childPrivacyService.isUserUnder13(userAgeGroup);
          
          if (isUnder13) {
            return (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="h-5 w-5 text-sports-green" />
                    Privacy Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center text-center space-y-4 py-4">
                    <div className="w-16 h-16 bg-sports-green/20 rounded-full flex items-center justify-center">
                      <UserCheck className="h-8 w-8 text-sports-green" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">Privacy Settings Managed by Parent</h3>
                      <p className="text-gray-400 mt-2">
                        Your parent or guardian controls your profile's privacy settings to help keep you safe online.
                      </p>
                      <p className="text-gray-500 text-sm mt-2">
                        If you'd like to change your privacy settings, please ask your parent or guardian.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }
          
          return (
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Privacy Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <Label className="text-white font-medium">Make Profile Public</Label>
                    <p className="text-sm text-gray-400">Allow others to view your profile</p>
                  </div>
                  <Switch
                    checked={formData.show_profile_public}
                    onCheckedChange={(checked) => handleInputChange('show_profile_public', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <Label className="text-white font-medium">Show Contact Information Publicly</Label>
                    <p className="text-sm text-gray-400">Display phone number and contact info to public viewers</p>
                    <p className="text-sm text-red-400 mt-1">⚠️ Only enable if you want your contact information visible to everyone</p>
                  </div>
                  <Switch
                    checked={formData.show_contact_public}
                    onCheckedChange={(checked) => handleInputChange('show_contact_public', checked)}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className="bg-sports-green text-black hover:bg-green-600"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Player Profile
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EnhancedPlayerProfileForm;
